import {
	ComparisonOperatorDTOKeywordEnum,
	ConfidenceLevelEnum,
	type ParameterizedConceptDTORelativeness,
	ParameterizedConceptDTORepresentationModeEnum,
	SourceEnum,
	TimeHorizonEnum,
	type ConceptDefinitionDTO,
} from "@eu.mdotm.api/smart-workspaces";
import z from "zod";

/**
 * A concept definition as the concept select hands it back: a tag concept carries the tags it can be drilled
 * into, which the chain needs in order to render the next step and to name a breakdown.
 */
type RichConceptDefinitionDTO = ConceptDefinitionDTO & { availableTagsConcepts?: ConceptDefinitionDTO[] };

/**
 * # The form model is the schema
 *
 * A panel's fields used to be declared twice: a TS type for the form model and a zod schema for the
 * resolver. The type is now **derived** from the schema — every form model in this area is a `z.infer` of a
 * zod object like this one — because the two lists could disagree and the disagreement was invisible:
 * `zodResolver` hands the submit handler zod's *parsed* object, and a zod object deletes the keys it does
 * not declare. The type said the field was there; the value was already gone. That is how the "Show legend"
 * checkbox came to do nothing and how a chart's `description` was dropped, and neither the type checker nor
 * a test could see either.
 *
 * So: this object is the field list, `WorkspaceChain` is what it means, and every schema that validates a
 * chain is built by *extending* it (see `chainSubmitSchema` in `./schema`). A field can no longer be present
 * in one restatement and missing from the other, because there is no second restatement to omit it from.
 *
 * **What is deliberately not here** is validation that only applies at *submit* time. What a form model
 * holds while it is being filled in is a different question from what a panel accepts when the user presses
 * Apply, and the two genuinely differ — `name` is empty until a concept is picked, and a half-answered chain
 * has to be representable or the chain could not render it. Those rules live in `./schema`, applied on top
 * of this shape rather than beside it.
 *
 * `concept`, `availableTagsConcepts` and `parameterizedRelativeness` hold generated DTOs and are carried
 * through untouched (`z.custom` is a passthrough that supplies the type). `./schema` is where the two
 * concept-shaped ones are also validated *structurally* at submit time; the DTO stays the source of their
 * **type**, because it declares every field optional and an inferred shape would quietly disagree with it.
 */
const workspaceChainModel = z.object({
	conceptUuid: z.string().uuid().optional(),
	concept: z.custom<ConceptDefinitionDTO>().optional(),
	name: z.string().optional(),

	// time horizon
	timeHorizon: z.nativeEnum(TimeHorizonEnum).optional(),
	// confidenceLevel
	confidenceLevel: z.nativeEnum(ConfidenceLevelEnum).optional(),

	// relativeness
	representationMode: z.nativeEnum(ParameterizedConceptDTORepresentationModeEnum).optional(),
	parameterizedRelativeness: z.custom<ParameterizedConceptDTORelativeness>().optional(),

	// source enum
	source: z.nativeEnum(SourceEnum).optional(),

	// exposure options
	availableTagsConcepts: z.array(z.custom<ConceptDefinitionDTO>()).optional(),
	fakeConceptId: z.string().optional(),

	/**
	 * replace parametrization of exposureOption & riskModelExposureLevels
	 *
	 * for tag exposure option
	 * @example
	 * ```js
	 * const parameters = {
	 *   tagExposureOption: "Equity"
	 * }
	 * ```
	 *
	 * for tag exposure risk model tags
	 * @example
	 * ```js
	 * const parameters ={
	 *   macroAssetClass: "Equity"
	 *   microAssetClass: "Information Technologies",
	 *   geography: "Europe",
	 *   microGeography: "Italy",
	 *   currency: "EUR"
	 * }
	 * ```
	 */
	parameters: z
		.object({
			tagExposureOption: z.string().optional(),
			macroAssetClass: z.string().optional(),
			microAssetClass: z.string().optional(),
			geography: z.string().optional(),
			microGeography: z.string().optional(),
			currency: z.string().optional(),
		})
		.optional(),

	/**
	 * The level the user asked to see *expanded* instead of pinned — set by picking the "All" leaf in the
	 * exposure chain (see `breakdownAllOption`). Mutually exclusive with a `parameters` pin on the same
	 * level by construction: they are the same select.
	 *
	 * Only the chart chain can produce one, and only a stacked column can draw it.
	 */
	breakdown: z.object({ dimension: z.string().min(1) }).optional(),
});

type WorkspaceChain = z.infer<typeof workspaceChainModel>;

const workspaceChainPredicateValueModel = z.union([z.boolean(), z.string(), z.number()]);

/**
 * The condition half of a filter's chain: an operator, and the values it applies to.
 *
 * The tuple says how many values the operator takes and whether each is a list, and every position is
 * nullable because that is what the control produces while it is being filled in — `btw` starts as
 * `[null, null]`. Submitting with a hole in it is what {@link workspaceChainPredicateSubmitShape} rejects.
 */
const workspaceChainPredicateModel = z.object({
	comparisonMode: z.nativeEnum(ComparisonOperatorDTOKeywordEnum).optional(),
	comparisonValue: z
		.union([
			z.tuple([workspaceChainPredicateValueModel.nullable()]),
			z.tuple([workspaceChainPredicateValueModel.nullable(), workspaceChainPredicateValueModel.nullable()]),
			z.tuple([z.array(workspaceChainPredicateValueModel).nullable()]),
			z.tuple([
				z.array(workspaceChainPredicateValueModel).nullable(),
				z.array(workspaceChainPredicateValueModel).nullable(),
			]),
		])
		.optional(),
});

type WorkspaceChainPredicate = z.infer<typeof workspaceChainPredicateModel>;

export { workspaceChainModel, workspaceChainPredicateModel, workspaceChainPredicateValueModel };
export type { RichConceptDefinitionDTO, WorkspaceChain, WorkspaceChainPredicate };
